// Diary drafts — one key per diary, per field.
//
// W54.0 A1. The keys used to be `diary-title:edit` / `diary-content:edit`:
// scoped to the **mode** and to nothing else. Two consequences, both of them
// data damage rather than cosmetics:
//
//   1. A leftover draft from diary A surfaced in diary B's editor — on a cold
//      load, where the server's copy has not arrived yet — and could be saved
//      into B.
//   2. The legacy keys are therefore **invalidated** rather than migrated.
//      Those drafts were never attributable to a diary, so keeping them is
//      keeping the pollution alive. This is a deliberate, documented
//      trade-off, not an accident: `clearLegacyDiaryDrafts()` wipes them the
//      first time an edit route mounts.
//
// The create-mode keys are unchanged (`diary-title:create` /
// `diary-content:create`): there is exactly one 「new diary」 and its draft is
// the interrupted writing, which is the entire reason a draft exists.
//
// `useDraft` owns the `heshe.draft.` prefix; the keys here are the unprefixed
// part, so the full storage key of an edit draft is
// `heshe.draft.diary-content:edit:<id>`.

import { clearDraft } from "@/lib/hooks/useDraft";

export type DiaryDraftMode = "create" | "edit";
export type DiaryDraftField = "title" | "content";

/**
 * The unscoped edit-mode keys W54.0 A1 invalidated.
 *
 * Kept as a named export because "which keys did we throw away" is a question
 * the next reader asks, and the answer should not be a grep.
 */
export const LEGACY_EDIT_DRAFT_KEYS = ["diary-title:edit", "diary-content:edit"] as const;

/**
 * The draft key for one field of one diary.
 *
 * Edit mode is scoped by the diary id, so diary A's draft cannot surface in
 * diary B's editor — the second half of the A1 defect, and the one that
 * survives the route-level guard on its own.
 *
 * An edit-mode editor without an id cannot be reached: the route disables the
 * query when the id is empty, so the editor never mounts. The empty suffix is
 * the belt-and-braces answer — it collides with no real diary's key, and with
 * no legacy key either.
 */
export function diaryDraftKey(
  field: DiaryDraftField,
  mode: DiaryDraftMode,
  diaryId?: string | null,
): string {
  if (mode === "create") return `diary-${field}:create`;
  const id = diaryId?.trim() ?? "";
  return `diary-${field}:edit${id ? `:${id}` : ""}`;
}

/** Both draft keys for one editor. */
export function diaryDraftKeys(
  mode: DiaryDraftMode,
  diaryId?: string | null,
): { title: string; content: string } {
  return {
    title: diaryDraftKey("title", mode, diaryId),
    content: diaryDraftKey("content", mode, diaryId),
  };
}

/**
 * Wipe the unscoped edit-mode drafts.
 *
 * Called when an edit route mounts. Nothing is migrated: an unscoped draft
 * cannot be attributed to a diary, and attributing it to the diary that
 * happens to be open is exactly the defect this key change removes.
 */
export function clearLegacyDiaryDrafts(): void {
  for (const key of LEGACY_EDIT_DRAFT_KEYS) clearDraft(key);
}
